'use client';

import {
  Box,
  Button,
  Fade,
  FormControl,
  FormControlLabel,
  FormLabel,
  Paper,
  Popper,
  Select,
  Stack,
  Switch,
  Tooltip,
  Typography,
  MenuItem as MuiMenuItem,
  type IconProps,
} from '@mui/material';
import { Provider } from '@repo/api-gw-sdk';
import classNames from 'classnames';
import { usePathname } from 'next/navigation';
import React, { useContext, useRef, useState } from 'react';

import { AiStarBig } from '@/components/assistant-ui/assets/aiStarBig';
import ButtonWithIcon from '@/components/buttons/buttonWithIcon';
import { Icon } from '@/components/shared/icon';
import { Link } from '@/components/shared/link';
import { ThemeContext } from '@/components/theme';
import { useEnvironment } from '@/contexts/useEnvironment';
import { CloudProviders } from '@/data/inventory/data';

export const AssistantMenuItem = ({
  href,
  title,
}: {
  title: string;
  href: string;
}) => {
  const pathname = usePathname();
  const selected = pathname.startsWith(href);

  return (
    <Stack className='mb-[40px]' gap='4px'>
      <Box
        sx={{
          background: 'linear-gradient(90deg, #C9EBAE 0%, #0073B0 100%)',
          height: '1px',
          width: '100%',
        }}
      />
      <Link
        href={href}
        className={classNames('slide-left h-[60px] min-h-[60px]', {
          'bg-white': selected,
          'text-[var(--mui-palette-background-black1)]': selected,
          'hover:bg-[rgba(255,255,255,0.10)]': !selected,
        })}
      >
        <Box className='w-[24px] h-[24px] ml-[14px] mt-[-8px]'>
          <AiStarBig />
        </Box>
        <Typography
          className={classNames(
            'slide-left collapse absolute left-[60px] w-[200px] opacity-0',
            {
              'text-[var(--mui-palette-background-black1)]': selected,
              'text-white': !selected,
            }
          )}
        >
          {title}
        </Typography>
      </Link>
      <Box
        sx={{
          background: 'linear-gradient(90deg, #C9EBAE 0%, #0073B0 100%)',
          height: '1px',
          width: '100%',
        }}
      />
    </Stack>
  );
};

export const MenuItem = ({
  href,
  title,
  iconClass,
  testId,
  disabled,
}: {
  title: string;
  href: string;
  iconClass: string;
  testId?: string;
  disabled?: boolean;
}) => {
  const pathname = usePathname();
  const selected = pathname.startsWith(href);

  return disabled ? (
    <Box
      className={`${classNames(
        'slide-left',
        {}
      )} w-[60px] h-[60px] min-h-[60px] cursor-default flex items-center justify-middle`}
    >
      <i
        className={`${iconClass} w-[24px] h-[24px] ml-[18px] text-[#a1a1aa]`}
      />
      <Typography
        className={classNames(
          'slide-left collapse absolute left-[60px] w-[200px] opacity-0 text-[#a1a1aa]'
        )}
      >
        {title}
      </Typography>
    </Box>
  ) : (
    <Link
      href={href}
      data-testid={testId}
      className={classNames('slide-left h-[60px] min-h-[60px]', {
        'bg-white': selected,
        'text-[var(--mui-palette-background-black1)]': selected,
        'hover:bg-[rgba(255,255,255,0.10)]': !selected,
      })}
    >
      <i className={`${iconClass} w-[24px] h-[24px] ml-[18px]`} />
      <Typography
        className={classNames(
          'slide-left collapse absolute left-[60px] w-[200px] opacity-0',
          {
            'text-[var(--mui-palette-background-black1)]': selected,
            'text-white': !selected,
          }
        )}
      >
        {title}
      </Typography>
    </Link>
  );
};

interface ActionIconProps extends IconProps {
  title: string;
  isActive: boolean;
  iconClass: string;
  onClick: () => void;
}

const ActionIcon = ({
  title,
  isActive,
  iconClass,
  onClick,
  ...rest
}: ActionIconProps) => {
  return (
    <Tooltip title={title} PopperProps={{ disablePortal: true }}>
      <Icon
        {...rest}
        sx={{
          backgroundColor: isActive
            ? 'white'
            : 'var(--mui-palette-background-black1)',

          '&:hover': {
            backgroundColor: isActive ? 'white' : 'rgba(255, 255, 255, 0.10)',
          },

          '& i': {
            color: isActive ? 'var(--mui-palette-background-black1)' : 'white',
          },
        }}
        iconClass={iconClass}
        isActive={isActive}
        onClick={onClick}
      />
    </Tooltip>
  );
};

export const NavigationActions = ({
  setForceOpen,
}: {
  setForceOpen: (open: boolean) => void;
}) => {
  const { theme, changeTheme } = useContext(ThemeContext);
  const isDarkMode = theme === 'dark';
  const { isDemo, isDev } = useEnvironment();

  return (
    <Box className='navigation-actions slide-left collapse opacity-0 absolute left-[24px] right-[24px] bottom-[12px] '>
      <Stack
        direction='row'
        className='w-[257px]'
        alignItems='center'
        justifyContent='space-between'
      >
        <Link href='/docs' target='_blank' className='w-auto'>
          <ButtonWithIcon
            text='Eon Docs'
            data-testid='eon-docs'
            variant='outlined'
            icon='material-symbols-help-outline-rounded'
            sx={{
              borderColor: 'white',
              background: 'transparent',
              color: 'white',

              '&:hover': {
                color: 'white !important',
                backgroundColor: 'rgba(255, 255, 255, 0.10) !important',
              },
            }}
          />
        </Link>
        {(isDemo || isDev) && <DemoSettingsMenu setForceOpen={setForceOpen} />}
        <Stack direction='row' gap='12px'>
          <ActionIcon
            data-testid='light-mode'
            title='Light mode'
            isActive={!isDarkMode}
            iconClass='material-symbols-light-mode-outline-rounded'
            onClick={() => changeTheme('light')}
          />
          <ActionIcon
            data-testid='dark-mode'
            title='Dark mode'
            isActive={isDarkMode}
            iconClass='material-symbols-bedtime-outline-rounded'
            onClick={() => changeTheme('dark')}
          />
        </Stack>
      </Stack>
    </Box>
  );
};

const DemoSettingsMenu = ({
  setForceOpen,
}: {
  setForceOpen: (open: boolean) => void;
}) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const { demoSettings, updateDemoSettings } = useEnvironment();

  return (
    <>
      <Tooltip title='Console Settings'>
        <Icon
          ref={anchorRef}
          onClick={(event) => {
            event.stopPropagation();
            setOpen(!open);
            setForceOpen(!open);
          }}
          iconClass='material-symbols-settings-outline-rounded'
          sx={{
            cursor: 'pointer',
            backgroundColor: 'var(--mui-palette-background-black1)',

            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.10)',
            },

            '& i': {
              color: 'white',
            },
          }}
        />
      </Tooltip>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        transition
        disablePortal
        sx={{ zIndex: 5 }}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps}>
            <Paper className={'shadow-lg'}>
              <Stack
                onClick={(event) => event.stopPropagation()}
                sx={{
                  width: '250px',
                  padding: '24px',
                }}
                gap='24px'
              >
                <Stack
                  direction='row'
                  alignItems='center'
                  justifyContent='space-between'
                >
                  <Typography variant='h3'>Console Settings</Typography>
                  <Icon
                    iconClass='material-symbols-close-rounded'
                    onClick={() => {
                      setOpen(false);
                      setForceOpen(false);
                    }}
                  />
                </Stack>
                <Stack gap='24px'>
                  <FormControl>
                    <FormLabel>Primary Cloud</FormLabel>
                    <Select
                      value={demoSettings.primaryProvider}
                      onChange={(event) =>
                        updateDemoSettings({
                          ...demoSettings,
                          primaryProvider: event.target.value as
                            | Provider.Aws
                            | Provider.Azure
                            | Provider.Gcp,
                        })
                      }
                    >
                      {(
                        [Provider.Aws, Provider.Azure, Provider.Gcp] as const
                      ).map((provider) => (
                        <MuiMenuItem key={provider} value={provider}>
                          {CloudProviders[provider].displayName}
                        </MuiMenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl>
                    <FormLabel>Clouds</FormLabel>
                    {(
                      [
                        Provider.Aws,
                        Provider.Azure,
                        Provider.Gcp,
                        Provider.MongoAtlas,
                      ] as const
                    ).map((provider) => (
                      <FormControlLabel
                        className='my-[6px]'
                        key={provider}
                        control={<Switch className='mr-[12px]' />}
                        label={CloudProviders[provider].displayName}
                        checked={demoSettings[provider]}
                        onChange={(event, checked) => {
                          const updatedSettings = {
                            ...demoSettings,
                            [provider]: checked,
                          };

                          if (
                            !updatedSettings[updatedSettings.primaryProvider]
                          ) {
                            updatedSettings.primaryProvider =
                              (
                                [
                                  Provider.Aws,
                                  Provider.Azure,
                                  Provider.Gcp,
                                ] as const
                              ).find((x) => updatedSettings[x]) || Provider.Aws;
                          }

                          updateDemoSettings(updatedSettings);
                        }}
                      />
                    ))}
                  </FormControl>
                  <FormControl>
                    <FormLabel>Features</FormLabel>
                    <FormControlLabel
                      className='my-[6px]'
                      control={<Switch className='mr-[12px]' />}
                      label='AI'
                      checked={demoSettings.ai}
                      onChange={(event, checked) =>
                        updateDemoSettings({
                          ...demoSettings,
                          ai: checked,
                        })
                      }
                    />
                    <FormControlLabel
                      className='my-[6px]'
                      control={<Switch className='mr-[12px]' />}
                      label='Ransomware'
                      checked={demoSettings.ransomware}
                      onChange={(event, checked) =>
                        updateDemoSettings({
                          ...demoSettings,
                          ransomware: checked,
                        })
                      }
                    />
                  </FormControl>
                  <Button
                    className='mt-[12px]'
                    variant='outlined'
                    onClick={() => window.location.reload()}
                  >
                    Reload
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          </Fade>
        )}
      </Popper>
    </>
  );
};
